import React from "react";
import { RankingTable } from "@/components/RankingTable";
import { useRanking } from "@/context/RankingContext";
import { useQuery } from "@tanstack/react-query";
import { fetchUniversityRankings } from "@/services/universityService";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";

/**
 * This page is for /rankings.
 * Shows a table of universities around DePaul's rank.
 */

export function RankingsPage() {
  const { 
    isSimulationMode, 
    simulatedScore, 
    depaulRank 
  } = useRanking();

  const { data: rankings, isLoading } = useQuery({
    queryKey: ["rankings", isSimulationMode, simulatedScore],
    queryFn: fetchUniversityRankings,
  });

  const calculateTableData = () => {
    if (!rankings) return { tableData: [], hasScoreChanged: false };

    // Convert rankings to table data format
    const allData = rankings.map((uni: any) => ({
      rank: Number(uni.national_rank) || 0,
      name: uni.university,
      score: Number(uni.overall_score) || 0
    }));

    const depaulIndex = allData.findIndex(uni => uni.name === 'DePaul University');
    const hasScoreChanged = isSimulationMode && 
      simulatedScore !== null && 
      depaulIndex !== -1 && 
      simulatedScore !== allData[depaulIndex].score;

    if (hasScoreChanged && depaulIndex !== -1 && simulatedScore !== null) {
      // Update DePaul's score and rank
      allData[depaulIndex].score = simulatedScore;
      allData[depaulIndex].rank = depaulRank; // Use the rank from context

      // Sort by score in descending order
      allData.sort((a, b) => b.score - a.score);
      
      // Get the slice of universities around DePaul's new position
      const newDepaulIndex = allData.findIndex(uni => uni.name === 'DePaul University');
      const startIndex = Math.max(0, newDepaulIndex - 5);
      const endIndex = Math.min(allData.length, newDepaulIndex + 6);
      
      return {
        tableData: allData.slice(startIndex, endIndex),
        hasScoreChanged: true
      };
    }

    // If not in simulation mode, just sort by original rank
    allData.sort((a, b) => a.rank - b.rank);
    
    // Get the slice of universities around DePaul
    const startIndex = Math.max(0, depaulIndex - 5);
    const endIndex = Math.min(allData.length, depaulIndex + 6);
    
    return {
      tableData: allData.slice(startIndex, endIndex),
      hasScoreChanged: false
    };
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="border-[#4FAADD]/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center h-32">
              <p className="text-gray-500">Loading Rankings...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!rankings) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="border-[#4FAADD]/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center h-32">
              <p className="text-gray-500">No ranking data found.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { tableData, hasScoreChanged } = calculateTableData();

  return (
    <div className="container mx-auto px-4 py-8">
      <Card className="border-[#4FAADD]/20">
        <CardHeader>
          <CardTitle className="text-3xl font-bold text-[#0057B7]">
            University Rankings
          </CardTitle>
          <CardDescription className="text-base mt-2">
            View rankings of universities around DePaul's position. The table shows relative performance and scores.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RankingTable
            universities={tableData}
            isSimulationMode={isSimulationMode}
            hasScoreChanged={hasScoreChanged}
          />
        </CardContent>
      </Card>
    </div>
  );
}